/**
 * getActiveSelection — find what the user currently has highlighted.
 *
 * Returns one of:
 *   - { kind: 'input', text, node, start, end }       for INPUT/TEXTAREA
 *   - { kind: 'range', text, range }                  for arbitrary DOM
 *   - null when there's no usable selection
 *
 * Special case: an INPUT/TEXTAREA whose ENTIRE contents are short enough
 * (<4000 chars) is treated as the implicit selection so the user doesn't
 * have to triple-click before invoking ⌘.
 */
export function getActiveSelection() {
  const active = document.activeElement;
  if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
    const start = active.selectionStart;
    const end = active.selectionEnd;
    if (start != null && end != null && start !== end) {
      return { kind: 'input', text: active.value.slice(start, end), node: active, start, end };
    }
    if (active.value && active.value.trim().length > 0 && active.value.length < 4000) {
      return { kind: 'input', text: active.value, node: active, start: 0, end: active.value.length };
    }
  }
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const text = sel.toString();
    if (text.trim().length > 0) {
      return { kind: 'range', text, range: sel.getRangeAt(0) };
    }
  }
  return null;
}

/**
 * Position the popover near the selection. Centered fallback if the
 * selection has no measurable client rect.
 */
export function computePopoverPosition(snapshot) {
  let top = window.innerHeight / 2;
  let left = window.innerWidth / 2;
  try {
    let rect;
    if (snapshot.kind === 'range') rect = snapshot.range.getBoundingClientRect();
    else if (snapshot.node) rect = snapshot.node.getBoundingClientRect();
    if (rect && rect.width > 0) {
      top = Math.min(rect.bottom + 8, window.innerHeight - 340);
      left = Math.min(Math.max(rect.left, 12), window.innerWidth - 320);
    }
  } catch { /* DOMRect lookup fails on detached nodes — keep centered fallback */ }
  return { top, left };
}

/**
 * Replace a snapshot's selection with the rewritten text in-place.
 * Returns true on success.
 */
export function replaceSelectionInPlace(snapshot, rewritten) {
  if (snapshot.kind !== 'input') return false;
  const el = snapshot.node;
  const before = el.value.slice(0, snapshot.start);
  const after = el.value.slice(snapshot.end);
  const proto = el.tagName === 'TEXTAREA'
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const valueSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (valueSetter) valueSetter.call(el, before + rewritten + after);
  else el.value = before + rewritten + after;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

export const REWRITE_STYLES = [
  { key: 'shorten',      label: 'Shorten' },
  { key: 'formalize',    label: 'Formalize' },
  { key: 'casual',       label: 'Casual' },
  { key: 'summarize',    label: 'Summarize' },
  { key: 'action_items', label: 'Action items' },
];
